import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loading, EmptyState } from "@/components/common/Loading";
import { REACTIONS, REACTION_MAP } from "@/lib/reactions";
import { useReactors } from "@/hooks/useReactors";
import { ReactorRow } from "@/components/social/reactions/ReactorRow";
import { cn } from "@/lib/utils";

const isDesktop = () =>
  typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;

/**
 * The canonical reaction viewer: a right-side drawer on desktop, a tall bottom sheet on
 * mobile. Filters inside are FILTERS — they never give or change a reaction.
 */
export function ReactionViewerSheet({ open, onOpenChange, target, initialEmoji = null, summary }) {
  const [emoji, setEmoji] = useState(initialEmoji);
  const [desktop, setDesktop] = useState(isDesktop);
  const sentinel = useRef(null);
  const { items, summary: liveSummary, total, hasMore, loading, loadMore } =
    useReactors(target, emoji, open);

  useEffect(() => { if (open) setEmoji(initialEmoji || null); }, [open, initialEmoji]);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setDesktop(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loading) return;
    const io = new IntersectionObserver((e) => e[0].isIntersecting && loadMore(), { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadMore]);

  const counts = liveSummary || summary || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);
  const tabs = REACTIONS.filter((r) => counts[r.key]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={desktop ? "right" : "bottom"}
        className={cn("flex flex-col gap-0 p-0",
          desktop ? "w-full sm:max-w-md" : "h-[85vh] rounded-t-2xl")}
        data-testid="reaction-viewer-sheet"
      >
        <SheetHeader className="border-b border-border px-5 py-4 text-left">
          <SheetTitle>Reactions</SheetTitle>
        </SheetHeader>

        <div className="flex flex-wrap gap-1.5 border-b border-border px-4 py-3" role="tablist" aria-label="Filter reactions">
          <button
            type="button" role="tab" aria-selected={!emoji} onClick={() => setEmoji(null)}
            className={cn("rounded-full px-3 py-1 text-sm font-[560] transition-colors",
              !emoji ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:bg-secondary")}
            data-testid="reaction-filter-all"
          >
            All {totalAll}
          </button>
          {tabs.map((r) => (
            <button
              key={r.key} type="button" role="tab" aria-selected={emoji === r.key}
              aria-label={`${r.label} ${counts[r.key]}`}
              onClick={() => setEmoji(r.key)}
              className={cn("inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-[560] transition-colors",
                emoji === r.key ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:bg-secondary")}
              data-testid={`reaction-filter-${r.key}`}
            >
              <span className="text-base leading-none">{r.emoji}</span> {counts[r.key]}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
          {loading && items.length === 0 && <Loading />}
          {!loading && items.length === 0 && (
            <EmptyState title="No reactions yet" description={
              emoji ? `Nobody has reacted with ${REACTION_MAP[emoji]?.label || "this"} yet.` : "Be the first to react."} />
          )}
          {items.map((p) => (
            <ReactorRow key={`${p.user_id}-${p.emoji}`} person={p} onNavigate={() => onOpenChange(false)} />
          ))}
          <div ref={sentinel} />
          {loading && items.length > 0 && (
            <p className="py-3 text-center text-xs text-muted-foreground" data-testid="reaction-viewer-loading-more">Loading…</p>
          )}
          {!hasMore && items.length > 0 && (
            <p className="py-3 text-center text-xs text-muted-foreground" data-testid="reaction-viewer-end">
              {total} {total === 1 ? "person" : "people"}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ReactionViewerSheet;
